<template>
  <div>
    <div
      v-if="activeUsers.length > 0"
      class="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-200"
    >
      <p class="text-center text-sm font-medium py-2">{{ activeUsers.length }} exploring now</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading && activeUsers.length === 0" class="text-center py-16">
      <Loader2 class="h-8 w-8 animate-spin mx-auto text-muted-foreground mb-4" />
      <p class="text-muted-foreground">Loading explorers...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="activeUsers.length === 0" class="text-center py-12">
      <div
        class="bg-rogue-vibrant-green/10 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4"
      >
        <Compass class="h-12 w-12 text-rogue-vibrant-green" />
      </div>
      <h2 class="font-semibold text-rogue-deep-forest mb-2">No one's out exploring yet</h2>
      <p class="text-gray-600 mb-4">
        Be the first spark! Start an exploration session and let others know you're out there.
      </p>
      <Button variant="primary" @click="$emit('start-exploring')">
        <Sparkles class="h-4 w-4 mr-2" />
        Start Exploring
      </Button>
    </div>

    <!-- User List -->
    <div v-else class="space-y-2">
      <UserListItem
        v-for="user in activeUsers"
        :key="user.id"
        :user="user"
        :show-exploration-message="true"
        :is-liked="isLiked(user.id)"
        @view-profile="$emit('view-profile', $event)"
        @toggle-like="$emit('toggle-like', $event)"
      />
    </div>

    <div
      v-if="showHint && activeUsers.length > 0"
      class="fixed bottom-20 inset-x-0 flex justify-center z-30"
    >
      <div
        class="flex items-center gap-2 bg-white border border-gray-200 rounded-lg shadow p-2 max-w-sm"
      >
        <Info class="h-4 w-4 text-muted-foreground" />
        <p class="text-xs text-muted-foreground flex-1">🔥 indicates actively exploring.</p>
        <button class="p-1 text-gray-500 hover:text-gray-700" @click="dismissHint">
          <X class="h-3 w-3" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Compass, Sparkles, Loader2, Info, X } from 'lucide-vue-next'
import { useUserStore } from '../../stores/user'
import Button from '../ui/Button.vue'
import UserListItem from '../UserListItem.vue'

defineProps({
  activeUsers: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

defineEmits(['toggle-like', 'view-profile', 'start-exploring'])

const userStore = useUserStore()
const showHint = ref(true)

onMounted(() => {
  if (localStorage.getItem('exploreHintDismissed') === 'true') {
    showHint.value = false
  }
})

function dismissHint() {
  showHint.value = false
  localStorage.setItem('exploreHintDismissed', 'true')
}

function isLiked(userId) {
  return userStore.likes.includes(userId)
}
</script>
